import { createContext } from 'react';
import PropTypes from 'prop-types';
import { math } from '@aexae/js_tools';
import { SafeArea } from '@capacitor-community/safe-area';
// import { alpha } from '@mui/material'; // TODO : uncomment when defining background mask based on custom primary
import Color from 'color';
import palette from '../../theme/palette';

import useLocalStorage from '../../hooks/useLocalStorage';

const COLORS = [
  {
    name: 'default',
    primary: palette.light.primary,
    secondary: palette.light.secondary
  }
];

const defaultColor = COLORS[0];

const SetColor = (themeColor) => {
  const PRIMARY_DEFAULT = defaultColor.primary;
  const SECONDARY_DEFAULT = defaultColor.secondary;

  let primary;
  let secondary;
  let colorPrimary = PRIMARY_DEFAULT.main;
  let colorSecondary = SECONDARY_DEFAULT.main;

  switch (themeColor) {
    case 'custom':
      try {
        const customPrimaryOmnis = localStorage.getItem('color_primary');
        const customSecondaryOmnis = localStorage.getItem('color_secondary');
        const customPrimary = Color(math.colorIntToHex(customPrimaryOmnis)).hex();
        const customSecondary = Color(math.colorIntToHex(customSecondaryOmnis)).hex();
        if (customPrimaryOmnis) {
          colorPrimary = Color(customPrimary).hex();
        }
        if (customSecondaryOmnis) {
          colorSecondary = Color(customSecondary).hex();
        }
      } catch (err) {
        console.warn(err);
        colorPrimary = PRIMARY_DEFAULT.main;
        colorSecondary = SECONDARY_DEFAULT.main;
      }

      primary = {
        ...PRIMARY_DEFAULT,
        name: 'custom',
        lightest: Color(colorPrimary).lightness(96).hex(), // TODO : workaround pour couleur client claires / foncées
        lighter: Color(colorPrimary).lightness(72).hex(),
        light: Color(colorPrimary).lightness(64).hex(),
        main: colorPrimary,
        dark: Color(colorPrimary).darken(0.32).hex(),
        darker: Color(colorPrimary).darken(0.56).hex(),
        darkest: Color(colorPrimary).darken(0.86).hex(),
        contrastText: Color(colorPrimary).isLight() ? '#000' : '#fff'
      };
      secondary = {
        ...SECONDARY_DEFAULT,
        name: 'custom',
        lighter: Color(colorSecondary).lighten(0.56).hex(),
        light: Color(colorSecondary).lighten(0.32).hex(),
        main: colorSecondary,
        dark: Color(colorSecondary).darken(0.32).hex(),
        darker: Color(colorSecondary).darken(0.56).hex(),
        darkest: Color(colorSecondary).darken(0.86).hex(),
        contrastText: Color(colorSecondary).isLight() ? '#000' : '#fff'
      };
      break;
    default:
      primary = PRIMARY_DEFAULT;
      secondary = SECONDARY_DEFAULT;
  }

  if (window.env.isApp) {
    const color = primary.main;
    const isLight = Color(color).isLight();

    const contentTheme = isLight ? 'dark' : 'light';

    SafeArea.enable({
      config: {
        customColorsForSystemBars: true,
        statusBarColor: color,
        statusBarContent: contentTheme,
        navigationBarColor: '#00000000',
        navigationBarContent: 'dark'
      }
    });
  }

  return { primary, secondary };
};
SetColor.propTypes = {
  themeColor: PropTypes.oneOf(['default', 'custom'])
};

const initialState = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeColor: 'default',
  themeStretch: true,
  onChangeMode: () => {},
  onChangeDirection: () => {},
  onChangeColor: () => {},
  onToggleStretch: () => {},
  setColor: defaultColor,
  colorOption: []
};

const SettingsContext = createContext(initialState);

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: initialState.themeMode,
    themeDirection: initialState.themeDirection,
    themeColor: initialState.themeColor,
    themeStretch: initialState.themeStretch
  });

  const onChangeMode = (event) => {
    setSettings({ ...settings, themeMode: event.target.value });
  };

  const onChangeDirection = (event) => {
    setSettings({ ...settings, themeDirection: event.target.value });
  };

  const onChangeColor = (event) => {
    setSettings({ ...settings, themeColor: event.target.value });
  };

  const onToggleStretch = () => {
    setSettings({ ...settings, themeStretch: !settings.themeStretch });
  };

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        // Mode
        onChangeMode,
        // Direction
        onChangeDirection,
        // Color
        onChangeColor,
        // Stretch
        onToggleStretch,
        setColor: SetColor(settings.themeColor),
        colorOption: COLORS.map((color) => ({ name: color.name, value: color.primary.main }))
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node
};

export { SettingsProvider, SettingsContext };
